import React, { useState } from "react";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { decodeToken } from "react-jwt";

import Button, { Color } from "../../../../../components/Form/Button";
import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { PublicFooter } from "../../../../../components/PublicFooter";
import { AppConfig } from "../../../../../constants";
import { Decoded } from "../../../../../models/decoded";
import UserService from "../../../../../services/user.service";
import { MainStack, Logo, GridItemInfo, GridItemForm } from "./styles";

interface Props {
  token?: string;
}

const LOGO = AppConfig.appLogo;
const LINK_EXPIRED_IMAGE = require("../../../../../assets/img/link-expired.svg");

const LinkExpired = ({ token }: Props) => {
  const { t: translate } = useTranslation();

  const [emailSent, setEmailSent] = useState<boolean>(false);

  const { mutate: sendConfirmationEmail, isLoading } = useMutation(
    ["resend.email"],
    (id: number) => UserService.resendConfirmationEmail(id),
    {
      onSuccess: () => {
        setEmailSent(true);
        NotificationToast.success(translate("confirmationEmailHasBeenSucessfullyResent"));
      },
      onError: () => {
        NotificationToast.error(translate("confirmationEmailCouldNotBeenResent"));
      },
    }
  );

  const handleResendConfirmationEmail = () => {
    if (token) {
      const decodedToken = decodeToken<Decoded>(token);

      if (!decodedToken?.id) return;

      sendConfirmationEmail(decodedToken?.id);
    }
  };

  const renderLeftSide = () => {
    return (
      <Stack direction="column" height="100%">
        <Logo src={LOGO} />
        <Stack alignItems="center" justifyContent="center" height="100%">
          <img src={LINK_EXPIRED_IMAGE} />
        </Stack>
      </Stack>
    );
  };

  const renderRightSide = () => {
    return (
      <Stack
        direction="column"
        height="100%"
        alignItems="flex-start"
        justifyContent="center"
        spacing={2}
      >
        <Stack spacing={1}>
          <Typography
            data-cy="expiredMessage"
            fontFamily="Manrope"
            fontWeight={700}
            fontSize="24px"
            lineHeight="34px"
            color="#D80027"
          >
            {translate("sorryTheLinkHasExpired")}
          </Typography>
          <Typography
            fontFamily="Manrope"
            fontWeight={500}
            fontSize="14px"
            lineHeight="21px"
            color="#465869"
          >
            {translate(
              "theEmailConfirmationLinkHasExpiredYouCanRequestANewOneByClickingOnTheButtonBelow"
            )}
          </Typography>
        </Stack>
        <Button
          data-cy="resendButton"
          loading={isLoading}
          disabled={emailSent}
          customColor={Color.SECONDARY}
          onClick={handleResendConfirmationEmail}
        >
          {emailSent ? translate("sent") : translate("resendConfirmationLink")}
        </Button>
      </Stack>
    );
  };

  return (
    <MainStack direction="column" alignItems="center" justifyContent="center" spacing={3}>
      <Box
        borderRadius="12px"
        boxShadow="2px 1px 5px rgba(152, 162, 179, 0.15), 0px 4px 10px rgba(48, 140, 219, 0.05);"
        width="100%"
        height="100%"
        overflow="auto"
      >
        <Grid container height="100%">
          <GridItemInfo item padding="52px 52px 0 52px" borderRadius="12px 0px 0px 12px" lg={6}>
            {renderLeftSide()}
          </GridItemInfo>
          <GridItemForm item md={12} lg={6} borderRadius="0px 12px 12px 0px">
            {renderRightSide()}
          </GridItemForm>
        </Grid>
      </Box>
      <PublicFooter />
    </MainStack>
  );
};

export default LinkExpired;
